import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./LogoManagementPage.module.scss";
import Header from "../Header/header";
import SetupSteps, { Step } from "../Register/SetupSteps/SetupSteps";
import axios from "axios";
import confetti from "canvas-confetti";

const STEPS: Step[] = [
  { key: "practice", label: "Practice and personal info" },
  { key: "logo", label: "Logo Management" },
  { key: "social", label: "Social Media Links" },
  { key: "portfolio", label: "Portfolio Gallery" },
  { key: "consent", label: "Consent Templates" },
  { key: "preview", label: "Preview & Complete" },
];

type UploadState = {
  file?: File;
  previewUrl?: string;
  error?: string;
};

function UploadBox({
  title,
  hint,
  accept = ".png,.jpg,.jpeg",
  uploadUrl,
  payloadKey,
}: {
  title: string;
  hint: string;
  accept?: string;
  uploadUrl: string;
  payloadKey: string;
}) {
  const [state, setState] = useState<UploadState>({});
  const [showModal, setShowModal] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onChoose = () => inputRef.current?.click();

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const celebrate = () => {
    const duration = 2 * 1000;
    const animationEnd = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
      });

      if (Date.now() < animationEnd) requestAnimationFrame(frame);
    };

    frame();
  };

  const uploadLogo = async (file: File) => {
    try {
      const base64 = await fileToBase64(file);

      await axios.put(
        uploadUrl,
        { [payloadKey]: base64 },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      celebrate();
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);
      resetDropzone();
    } catch (err) {
      alert("Failed to upload image. Please try again.");
      console.error("Upload error:", err);
    }
  };

  const resetDropzone = () => {
    setState({});
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const f = files[0];

    if (!accept.split(",").some((ext) => f.name.toLowerCase().endsWith(ext.trim()))) {
      alert("Invalid file type.");
      return;
    }

    if (f.size > 2 * 1024 * 1024) {
      alert("Max file size is 2MB.");
      return;
    }

    const previewUrl = URL.createObjectURL(f);
    setState({ file: f, previewUrl, error: undefined });
    uploadLogo(f);
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    handleFiles(e.target.files);
  };

  return (
    <div className={styles.uploadCard}>
      {showModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <div className={styles.celebrationIcon}>🎉</div>
            <h3 className={styles.modalTitle}>Upload successful!</h3>
            <p className={styles.modalSubtitle}>Your image has been saved.</p>
          </div>
        </div>
      )}

      <div
        className={styles.dropArea}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        aria-label={title}
      >
        <div className={styles.iconWrap} aria-hidden="true">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <path d="M12 16V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path
              d="M7 9l5-5 5 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M4 20h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>

        <div className={styles.uploadTitle}>{title}</div>
        <div className={styles.uploadHint}>{hint}</div>

        <button type="button" className={styles.chooseBtn} onClick={onChoose}>
          Choose file
        </button>

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className={styles.fileInput}
          onChange={onChange}
        />

        {state.previewUrl && (
          <div className={styles.previewWrap}>
            <img src={state.previewUrl} alt="Preview" className={styles.previewImg} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function LogoManagementPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await axios.get("/user");
        if (Array.isArray(data) && data.length > 1) {
          setUser(data[1]);
        }
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUser();
  }, []);

  return (
    <div className={styles.page}>
      <Header />

      <main className={styles.main}>
        <SetupSteps steps={STEPS} current={2} rightLabel="10% Complete" />

        <h1 className={styles.title}>Logo management</h1>
        <p className={styles.subtitle}>
          Add your practice logo (or personal brand) to make your portal truly yours. Your logo will appear on
          consent forms and patient materials, helping you look polished, consistent, and professional in every
          communication.
        </p>

        {/* ------------------ Practice logo upload ------------------ */}
        {user?.level !== "user" && (
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Practice logo*</h2>
          <UploadBox
            title="Upload practice logo"
            hint="PNG or JPG, max 2MB, 200x200px minimum"
            uploadUrl={`${process.env.REACT_APP_BASE_URL_V2}onboarding/update_practice_data`}
            payloadKey="logo"
          />
        </section>
      )}

        {/* ------------------ Personal logo (if practice level) ------------------ */}
        {/* user */}
        {user?.level === "user" && (
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Personal logo</h2>
            <UploadBox
              title="Upload personal logo or professional headshots"
              hint="PNG or JPG, max 2MB, 200x200px minimum"
              uploadUrl={`${process.env.REACT_APP_BASE_URL_V2}onboarding/update_practitioner_data`}
              payloadKey="logo"
            />

            <div className={`${styles.tipCard} ${styles.extraMargin}`}>
              <h3 className={styles.tipTitle}>A few tips for the best results:</h3>
              <ul className={styles.tips}>
                <li>Use a high-resolution image so your logo looks sharp across all platforms.</li>
                <li>
                  Remember, your logo will appear on consent forms and patient communications—a great way to
                  reinforce your brand.
                </li>
                <li>Choose a logo that’s versatile and professional, so it works well in different formats.</li>
                <li>You can update your logo anytime, keeping your look fresh and consistent.</li>
              </ul>
            </div>
          </section>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.btnSecondary} onClick={() => navigate("/practiceDetails")}>
            Back
          </button>
          <button type="button" className={styles.btnPrimary} onClick={() => navigate("/socialLinks")}>
            Next
          </button>
        </div>
      </main>

      <footer className={styles.footerBar}>Copyright © Medicube 2025. All Rights Reserved.</footer>
    </div>
  );
}
